// The pure side of the colour grader: what a look is made of, how a pixel is graded, how the page works out an automatic correction, and the looks themselves.
// `gradePixel` is the reference for the shader in gl.js: the two do the same sums (it is also what draws the look thumbnails and the histogram), and a test on the page compares them.
import { hexToRgb } from "../bgremove/backgrounds";

export const NEUTRAL = "#808080";
export const DEFAULTS = {
  exposure: 0, contrast: 0, highlights: 0, shadows: 0, fade: 0,
  temperature: 0, tint: 0, saturation: 1, vibrance: 0, hue: 0,
  lift: { hex: NEUTRAL, amount: 0 }, gamma: { hex: NEUTRAL, amount: 0 }, gain: { hex: NEUTRAL, amount: 0 },
  vignette: 0, grain: 0, sharpen: 0, lutStrength: 1, intensity: 1,
};

// The ranges of the sliders: [min, max, step].
export const RANGES = {
  exposure: [-2, 2, 0.05], contrast: [-1, 1, 0.02], highlights: [-1, 1, 0.02], shadows: [-1, 1, 0.02], fade: [0, 1, 0.02],
  temperature: [-1, 1, 0.02], tint: [-1, 1, 0.02], saturation: [0, 2, 0.02], vibrance: [-1, 1, 0.02], hue: [-180, 180, 1],
  vignette: [0, 1, 0.02], grain: [0, 1, 0.02], sharpen: [0, 1, 0.02], lutStrength: [0, 1, 0.02], intensity: [0, 1, 0.02],
};
const WHEELS = ["lift", "gamma", "gain"];

const clamp = (x, lo, hi) => Math.min(hi, Math.max(lo, x));
const num = (x, fallback) => (Number.isFinite(x) ? x : fallback);

// Looks of a person's own choosing are merged over the neutral settings; anything out of range or not a number is brought back.
export function mergeParams(given = {}) {
  const partial = given || {};
  const out = { ...DEFAULTS };
  Object.keys(RANGES).forEach((k) => { out[k] = clamp(num(partial[k], DEFAULTS[k]), RANGES[k][0], RANGES[k][1]); });
  WHEELS.forEach((w) => {
    const given = partial[w] || {};
    out[w] = { hex: /^#[0-9a-f]{6}$/i.test(given.hex || "") ? given.hex : NEUTRAL, amount: clamp(num(given.amount, 0), 0, 1) };
  });
  return out;
}

// What a colour wheel adds to red, green and blue: how far its colour is from grey, scaled by its strength. A neutral wheel adds nothing.
export function wheelVector({ hex, amount }) {
  const [r, g, b] = hexToRgb(hex);
  const mean = (r + g + b) / 3;
  return [(r - mean) * 2 * amount, (g - mean) * 2 * amount, (b - mean) * 2 * amount];
}

export const isNeutral = (p) => JSON.stringify(mergeParams(p)) === JSON.stringify(DEFAULTS);

const luma = (r, g, b) => 0.299 * r + 0.587 * g + 0.114 * b;
const gammaUp = (x) => Math.max(x, 0) ** 2.2;
const gammaDown = (x) => Math.max(x, 0) ** (1 / 2.2);

// Samples a 3D look-up table ({ size, data: Float32Array r-fastest }) at an RGB colour (0..1), blending the eight nearest entries.
export function sampleLut(lut, [r, g, b]) {
  const n = lut.size;
  const at = (v) => clamp(v, 0, 1) * (n - 1);
  const [x, y, z] = [at(r), at(g), at(b)];
  const [x0, y0, z0] = [Math.floor(x), Math.floor(y), Math.floor(z)];
  const [x1, y1, z1] = [Math.min(n - 1, x0 + 1), Math.min(n - 1, y0 + 1), Math.min(n - 1, z0 + 1)];
  const [fx, fy, fz] = [x - x0, y - y0, z - z0];
  const get = (i, j, k, c) => lut.data[((k * n + j) * n + i) * 3 + c];
  return [0, 1, 2].map((c) => {
    const lerp = (a, bb, t) => a + (bb - a) * t;
    const c00 = lerp(get(x0, y0, z0, c), get(x1, y0, z0, c), fx);
    const c10 = lerp(get(x0, y1, z0, c), get(x1, y1, z0, c), fx);
    const c01 = lerp(get(x0, y0, z1, c), get(x1, y0, z1, c), fx);
    const c11 = lerp(get(x0, y1, z1, c), get(x1, y1, z1, c), fx);
    return lerp(lerp(c00, c10, fy), lerp(c01, c11, fy), fz);
  });
}

// Grades one pixel (r, g, b in 0..1). `p` = mergeParams(...). `lut` is optional. Neither the vignette, the grain nor the sharpening is here: they depend on the neighbours or the position.
export function gradePixel(rgb, p, lut = null) {
  let [r, g, b] = rgb;
  if (p.exposure) { const k = 2 ** p.exposure; r = gammaDown(gammaUp(r) * k); g = gammaDown(gammaUp(g) * k); b = gammaDown(gammaUp(b) * k); }
  const t = p.temperature;
  const tint = p.tint;
  r *= 1 + 0.25 * t + 0.06 * tint;
  g *= 1 - 0.12 * tint;
  b *= 1 - 0.25 * t + 0.06 * tint;
  let y = clamp(luma(r, g, b), 0, 1);
  const tone = 1 + p.shadows * 0.6 * (1 - y) ** 2 + p.highlights * 0.6 * y * y;
  r *= tone; g *= tone; b *= tone;
  const scale = p.contrast >= 0 ? 1 + p.contrast : 1 + 0.7 * p.contrast;
  r = (r - 0.5) * scale + 0.5; g = (g - 0.5) * scale + 0.5; b = (b - 0.5) * scale + 0.5;
  y = clamp(luma(r, g, b), 0, 1);
  const lift = wheelVector(p.lift);
  const mid = wheelVector(p.gamma);
  const high = wheelVector(p.gain);
  const wl = (1 - y) ** 2;
  const wm = 4 * y * (1 - y);
  const wh = y * y;
  r += 0.25 * (lift[0] * wl + mid[0] * wm + high[0] * wh);
  g += 0.25 * (lift[1] * wl + mid[1] * wm + high[1] * wh);
  b += 0.25 * (lift[2] * wl + mid[2] * wm + high[2] * wh);
  y = luma(r, g, b);
  const sat = clamp(Math.max(r, g, b) - Math.min(r, g, b), 0, 1);
  const f = p.saturation * (1 + p.vibrance * (1 - sat));
  r = y + (r - y) * f; g = y + (g - y) * f; b = y + (b - y) * f;
  if (p.hue) {
    const a = (p.hue * Math.PI) / 180;
    const yy = luma(r, g, b);
    const i = 0.596 * r - 0.274 * g - 0.322 * b;
    const q = 0.211 * r - 0.523 * g + 0.312 * b;
    const i2 = i * Math.cos(a) - q * Math.sin(a);
    const q2 = i * Math.sin(a) + q * Math.cos(a);
    r = yy + 0.956 * i2 + 0.621 * q2; g = yy - 0.272 * i2 - 0.647 * q2; b = yy - 1.106 * i2 + 1.703 * q2;
  }
  r = r * (1 - 0.18 * p.fade) + 0.1 * p.fade; g = g * (1 - 0.18 * p.fade) + 0.1 * p.fade; b = b * (1 - 0.18 * p.fade) + 0.1 * p.fade;
  let out = [clamp(r, 0, 1), clamp(g, 0, 1), clamp(b, 0, 1)];
  if (lut && p.lutStrength > 0) {
    const l = sampleLut(lut, out);
    out = out.map((v, k) => v + (l[k] - v) * p.lutStrength);
  }
  return p.intensity >= 1 ? out : out.map((v, k) => rgb[k] + (v - rgb[k]) * p.intensity);
}

// ------------------------------------------------------------------------------------------ the looks
const wheel = (hex, amount) => ({ hex, amount });
export const LOOKS = {
  natural: { label: "Natural enhance", about: "A little more contrast and colour, and brighter shadows.", params: { contrast: 0.12, vibrance: 0.3, saturation: 1.04, highlights: -0.12, shadows: 0.18 } },
  cinematic: { label: "Cinematic teal & orange", about: "Cool shadows, warm skin and highlights: the blockbuster look.", params: { contrast: 0.22, saturation: 1.05, fade: 0.1, vignette: 0.28, lift: wheel("#2b8a96", 0.4), gain: wheel("#ff9a55", 0.38) } },
  golden: { label: "Golden hour", about: "Warm, glowing light.", params: { temperature: 0.45, tint: 0.05, exposure: 0.12, contrast: 0.08, saturation: 1.1, vignette: 0.2, gain: wheel("#ffb347", 0.3) } },
  vintage: { label: "Warm vintage", about: "Faded, warm and grainy, like an old film.", params: { temperature: 0.3, fade: 0.38, saturation: 0.82, contrast: -0.04, grain: 0.22, vignette: 0.32, lift: wheel("#6b5a3a", 0.3) } },
  cool: { label: "Cool blue", about: "Clean and cold, like an overcast morning.", params: { temperature: -0.42, saturation: 1.04, contrast: 0.1, shadows: 0.08 } },
  vivid: { label: "Vivid", about: "Rich, punchy colour.", params: { saturation: 1.38, vibrance: 0.3, contrast: 0.18, sharpen: 0.3 } },
  matte: { label: "Matte fade", about: "Lifted blacks and soft colour.", params: { fade: 0.55, contrast: -0.08, saturation: 0.9, highlights: -0.1 } },
  moody: { label: "Moody dark", about: "Dark, contrasty and a little blue.", params: { exposure: -0.35, contrast: 0.32, saturation: 0.8, vignette: 0.45, lift: wheel("#2a4a7a", 0.3) } },
  cyberpunk: { label: "Cyberpunk", about: "Magenta shadows, cyan highlights.", params: { contrast: 0.25, saturation: 1.22, vignette: 0.3, lift: wheel("#a02fd0", 0.45), gain: wheel("#31d0ff", 0.4) } },
  bw: { label: "Black & white", about: "Classic monochrome with strong contrast.", params: { saturation: 0, contrast: 0.26, vignette: 0.22, grain: 0.1 } },
  noir: { label: "Film noir", about: "Hard black and white, deep shadows and heavy grain.", params: { saturation: 0, contrast: 0.6, shadows: -0.2, highlights: 0.1, grain: 0.28, vignette: 0.55 } },
  sepia: { label: "Sepia", about: "Brown-toned monochrome.", params: { saturation: 0, contrast: 0.1, fade: 0.15, vignette: 0.25, lift: wheel("#6e4a24", 0.5), gamma: wheel("#b98a52", 0.5), gain: wheel("#f0cf9a", 0.4) } },
};
export const LOOK_IDS = Object.keys(LOOKS);
export const lookParams = (id) => mergeParams(LOOKS[id]?.params || {});

// ------------------------------------------------------------------------------------------ automatic correction
// Measures a picture: its average and extreme brightness, average colour and colourfulness. `rgba` = the bytes of an RGBA picture.
export function statsFromRGBA(rgba) {
  const hist = new Uint32Array(256);
  let sumY = 0; let sumR = 0; let sumG = 0; let sumB = 0; let sumS = 0;
  const n = rgba.length / 4;
  for (let i = 0; i < n; i += 1) {
    const r = rgba[i * 4] / 255; const g = rgba[i * 4 + 1] / 255; const b = rgba[i * 4 + 2] / 255;
    const y = luma(r, g, b);
    hist[Math.min(255, Math.round(y * 255))] += 1;
    sumY += y; sumR += r; sumG += g; sumB += b; sumS += Math.max(r, g, b) - Math.min(r, g, b);
  }
  const at = (p) => { let acc = 0; for (let v = 0; v < 256; v += 1) { acc += hist[v]; if (acc >= n * p) return v / 255; } return 1; };
  return { meanY: sumY / n, p1: at(0.01), p99: at(0.99), r: sumR / n, g: sumG / n, b: sumB / n, sat: sumS / n };
}

// The middle of several measurements (frames of one video).
export function combineStats(list) {
  const keys = ["meanY", "p1", "p99", "r", "g", "b", "sat"];
  const out = {};
  keys.forEach((k) => { const v = list.map((s) => s[k]).sort((a, b) => a - b); out[k] = v[Math.floor(v.length / 2)] ?? 0; });
  return out;
}

// Settings that correct a video from its measurements: brightness towards a pleasant middle, contrast towards the full range, a mild correction of a colour cast and of
// flat colour. Gentle on purpose (a sunset is meant to be orange): the result is a start for the sliders, not a verdict.
export function autoGrade(s) {
  if (!s || !(s.meanY > 0)) return mergeParams({});
  const exposure = clamp(Math.log2(0.46 / Math.max(0.03, s.meanY)) * 0.8, -1.2, 1.2);
  const range = Math.max(0.15, s.p99 - s.p1);
  const contrast = clamp((0.86 / range - 1) * 0.55, -0.1, 0.5);
  const q = (s.b + 0.01) / (s.r + 0.01);
  const temperature = clamp(((4 * (q - 1)) / (q + 1)) * 0.6, -0.5, 0.5);
  const q2 = (s.g + 0.01) / ((s.r + s.b) / 2 + 0.01);
  const tint = clamp(((q2 - 1) / (q2 + 1)) * 4 * 0.4, -0.4, 0.4);
  const saturation = clamp(0.28 / Math.max(0.05, s.sat), 0.9, 1.35);
  const shadows = s.p1 > 0.25 ? 0 : clamp((0.12 - s.p1) * 2, 0, 0.3);
  return mergeParams({ exposure, contrast, temperature, tint, saturation, shadows, highlights: s.p99 > 0.97 ? -0.15 : 0, vibrance: 0.15 });
}
